/**
 * MembershipCard.js — Potter's Vineyard wine-club membership card (cardType "membership").
 *
 * DOM parity with old createWineMembership (.card-box):
 *   cover → title → inner (price_additional_text, description bullets,
 *   Learn more, footer{price+unit, Join CTA}).
 *
 * LEARN MORE: emits ws-prompt-select with the card's `message` (old cardClicked
 * -> sendMessage), echoed as a user turn for the detailed view.
 *
 * JOIN NOW: always rendered (old createWineMembership never gated on
 * display_button / cta_link).
 *
 * Reference: RedConcierge/static/javascript/main.js createWineMembership() +
 *   index.css .card-box-title / .card-box-price / .learn-more / .innerfooter.
 */

import { emit } from "../../../core/state.js";
import { getDefaultCardLogo } from "../../../core/defaultCardLogos.js";
import { CHECK_ICON, esc } from "../../../components/base/MembershipCard.js";

// Old createWineMembership .learn-more icon (contact book with the person mark),
// verbatim path; stroke follows the pill's accent color.
const LEARN_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.66675 13.0002V3.00016C2.66675 2.55814 2.84234 2.13421 3.1549 1.82165C3.46746 1.50909 3.89139 1.3335 4.33341 1.3335H13.3334V14.6668H4.33341C3.89139 14.6668 3.46746 14.4912 3.1549 14.1787C2.84234 13.8661 2.66675 13.4422 2.66675 13.0002ZM2.66675 13.0002C2.66675 12.5581 2.84234 12.1342 3.1549 11.8217C3.46746 11.5091 3.89139 11.3335 4.33341 11.3335H13.3334M8.00008 6.66683C8.73646 6.66683 9.33341 6.06988 9.33341 5.3335C9.33341 4.59712 8.73646 4.00016 8.00008 4.00016C7.2637 4.00016 6.66675 4.59712 6.66675 5.3335C6.66675 6.06988 7.2637 6.66683 8.00008 6.66683ZM8.00008 6.66683C8.53051 6.66683 9.03922 6.87754 9.4143 7.25262C9.78937 7.62769 10.0001 8.1364 10.0001 8.66683M8.00008 6.66683C7.46965 6.66683 6.96094 6.87754 6.58587 7.25262C6.21079 7.62769 6.00008 8.1364 6.00008 8.66683" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export default class MembershipCard {
  render(club) {
    const card = document.createElement("div");
    card.className = "ws-card ws-membership-card";

    const openDetail = () => {
      const label =
        club.message || `Tell me more about ${club.title || "this membership"}`;
      emit("ws-prompt-select", {
        label,
        value: label,
        uniqueId: club.unique_id,
      });
    };

    const head = document.createElement("div");
    head.className = "ws-card-head";
    const img = document.createElement("img");
    img.className = "ws-card-img";
    img.loading = "lazy";
    img.alt = club.title || "Membership";
    img.style.cursor = "pointer";
    img.addEventListener("click", openDetail);
    if (club.cover_image_link) {
      img.src = club.cover_image_link;
    } else {
      img.src = getDefaultCardLogo();
      img.classList.add("ws-fg-mem-logo");
    }
    head.appendChild(img);
    card.appendChild(head);

    if (club.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = String(club.title).replace(/<br\s*\/?>/gi, " ");
      title.textContent = String(club.title).replace(/<br\s*\/?>/gi, "\n");
      card.appendChild(title);
    }

    // Old .innercardbox — price_additional_text + bullets + Learn more + footer.
    const inner = document.createElement("div");
    inner.className = "ws-rich-inner";

    // Old .card-box-price — e.g. "Refunded after 1-year commitment"
    if (club.price_additional_text) {
      const additional = document.createElement("p");
      additional.className = "ws-card-price ws-pot-mem-additional";
      additional.textContent = club.price_additional_text;
      inner.appendChild(additional);
    }

    const bullets = Array.isArray(club.description)
      ? club.description.filter(Boolean)
      : club.description
        ? [club.description]
        : [];
    if (bullets.length) {
      const list = document.createElement("ul");
      list.className = "ws-rich-bullets";
      bullets.forEach((text) => {
        const li = document.createElement("li");
        li.innerHTML = `<span class="ws-rich-check">${CHECK_ICON}</span><span>${esc(text)}</span>`;
        list.appendChild(li);
      });
      inner.appendChild(list);
    }

    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${LEARN_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    inner.appendChild(learn);

    // Old .innerfooter: price + unit LEFT, Join CTA RIGHT.
    const footer = document.createElement("div");
    footer.className = "ws-pot-mem-footer";

    const priceBlock = document.createElement("div");
    priceBlock.className = "ws-pot-mem-price";
    const amount = document.createElement("p");
    amount.className = "ws-card-price-amount";
    amount.textContent = club.price || "";
    priceBlock.appendChild(amount);
    const unit = document.createElement("p");
    unit.className = "ws-card-price-unit";
    unit.textContent = club.price_unit || "";
    priceBlock.appendChild(unit);
    footer.appendChild(priceBlock);

    const cta = document.createElement("a");
    cta.className = "ws-card-cta ws-fg-mem-cta";
    if (club.cta_link) {
      cta.href = club.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
    }
    cta.textContent = club.cta_text || "Join Now";
    footer.appendChild(cta);
    inner.appendChild(footer);

    card.appendChild(inner);
    return card;
  }
}
